import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Video, MapPin, User } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SessionEvent {
  id: string;
  client_id: string;
  scheduled_at: string;
  duration_minutes: number;
  session_type: string;
  status: string;
  price: number;
  meeting_url?: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

export const CalendarTab = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [sessions, setSessions] = useState<SessionEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, [user]);

  const loadSessions = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          profiles!sessions_client_id_fkey (
            first_name,
            last_name
          )
        `)
        .eq('therapist_id', user.id)
        .eq('status', 'scheduled')
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true });

      if (error) throw error;

      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const sessionDates = sessions.map(s => new Date(s.scheduled_at));
  const selectedDateSessions = sessions.filter(s =>
    selectedDate && isSameDay(new Date(s.scheduled_at), selectedDate)
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Your Session Calendar
            </CardTitle>
            <CardDescription>
              View and manage your scheduled sessions
            </CardDescription>
          </div>
          <Button asChild variant="outline">
            <Link to="/therapist-portal/availability">
              Manage Availability
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              modifiers={{
                booked: sessionDates
              }}
              modifiersStyles={{
                booked: {
                  fontWeight: 'bold',
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'white'
                }
              }}
            />
            <div className="mt-4 flex items-center gap-2 text-sm">
              <div className="w-4 h-4 bg-primary rounded"></div>
              <span className="text-muted-foreground">Days with sessions</span>
            </div>
          </div>

          {/* Sessions for selected date */}
          <div>
            <h3 className="font-semibold mb-3">
              {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a date'}
            </h3>
            {selectedDateSessions.length > 0 ? (
              <div className="space-y-3">
                {selectedDateSessions.map((session) => (
                  <div
                    key={session.id}
                    className="p-4 border rounded-lg bg-accent/50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {session.profiles.first_name} {session.profiles.last_name}
                        </span>
                      </div>
                      <Badge variant="secondary">{session.status}</Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm mb-3">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {format(new Date(session.scheduled_at), 'h:mm a')}
                      </span>
                      <span className="flex items-center gap-1">
                        {session.session_type === 'online' ? (
                          <><Video className="h-3 w-3" /> Online</>
                        ) : (
                          <><MapPin className="h-3 w-3" /> In Person</>
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        {session.duration_minutes} min
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-primary">${session.price}</span>
                      {session.session_type === 'online' && (
                        <Button size="sm">
                          <Video className="h-3 w-3 mr-1" />
                          Join Session
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {selectedDate ? 'No sessions scheduled for this day' : 'Select a date to see sessions'}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
